import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getAllServices } from '../../service/store/serviceSlice';
import { getServiceIconById } from '../../../utils/serviceIcons';
import { findMatchingServices, getSearchSuggestions, getPopularSearchTerms } from '../../../utils/searchUtils';
import { bookingNavigationState } from '../../booking/utils/bookingNavigationState';

/**
 * PredictiveSearch Component
 * Provides intelligent search suggestions and service matching
 */
const PredictiveSearch = ({ 
  placeholder = "What needs fixing? (e.g., water leak, broken AC)",
  className = "",
  onSearch = null,
  showPopularTerms = true,
  variant = "default", // "default" for gradient background, "white" for white background
  selectedAddress = null, // Add selectedAddress prop
  bookingIntent = null,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { services } = useSelector((state) => state.service);
  
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [matchingServices, setMatchingServices] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [popularTerms] = useState(getPopularSearchTerms());
  const [serviceSlugToIdMap, setServiceSlugToIdMap] = useState({});
  
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [chipSlot, setChipSlot] = useState(null);

  // Mount chips before paint so the hero slot does not grow after first frame.
  useLayoutEffect(() => {
    if (variant !== 'hero') return undefined;
    setChipSlot(document.getElementById('fx-popular-slot'));
    return undefined;
  }, [variant]);

  // Get icon for popular term based on the service it maps to
  const getPopularTermIcon = (term) => {
    const matches = findMatchingServices(term);
    if (matches.length > 0) {
      const bestMatch = matches[0];
      return getServiceIconById(bestMatch.serviceId, 'w-4 h-4 object-contain');
    }
    return getServiceIconById('plumbing', 'w-4 h-4 object-contain'); // Default icon
  };

  // Fetch services and create slug-to-ID mapping
  useEffect(() => {
    if (services.length === 0) {
      dispatch(getAllServices({ page: 1, limit: 50 }));
    }
  }, [dispatch, services.length]);

  // Create slug-to-ID mapping when services are loaded
  useEffect(() => {
    if (services.length > 0) {
      const slugMap = {};
      services.forEach(service => {
        if (service.slug) {
          slugMap[service.slug] = service._id;
        }
      });
      setServiceSlugToIdMap(slugMap);
    }
  }, [services]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    if (value.trim().length >= 2) {
      // Get text suggestions
      const textSuggestions = getSearchSuggestions(value);
      setSuggestions(textSuggestions);

      // Get matching services
      const services = findMatchingServices(value);
      setMatchingServices(services);

      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setMatchingServices([]);
      setShowSuggestions(false);
    }
  };

  // Handle search submission
  const handleSearch = (searchQuery = query) => {
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      
      if (onSearch) {
        onSearch(searchQuery);
      } else {
        navigate(`/services/search?q=${encodeURIComponent(searchQuery.trim())}`, {
          state: { selectedAddress }
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const openBooking = (serviceId, serviceLabel) => {
    const loaded = services.find((item) => item._id === serviceId);
    navigate(`/booking/new/${serviceId}`, {
      state: bookingNavigationState({
        service: loaded,
        intent: bookingIntent,
        selectedAddress,
        serviceLabel,
      }),
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    const totalItems = suggestions.length + matchingServices.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          setQuery(suggestions[selectedIndex]);
          handleSearch(suggestions[selectedIndex]);
        } else if (selectedIndex >= suggestions.length && selectedIndex < totalItems) {
          const serviceIndex = selectedIndex - suggestions.length;
          const service = matchingServices[serviceIndex];
          if (service) {
            setQuery(service.serviceName);
            const serviceId = serviceSlugToIdMap[service.serviceId];
            if (serviceId) {
              openBooking(serviceId, service.serviceName);
            } else {
              navigate(`/services/search?q=${encodeURIComponent(service.serviceName)}`, {
                state: { selectedAddress }
              });
            }
          }
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  // Handle service click
  const handleServiceClick = (service) => {
    setQuery(service.serviceName);
    const serviceId = serviceSlugToIdMap[service.serviceId];
    
    if (serviceId) {
      openBooking(serviceId, service.serviceName);
    } else {
      // Fallback to search results if ID not found
      navigate(`/services/search?q=${encodeURIComponent(service.serviceName)}`, {
        state: { selectedAddress }
      });
    }
  };

  // Handle popular term click
  const handlePopularTermClick = (term) => {
    setQuery(term);
    
    // Find the best matching service for this term
    const matches = findMatchingServices(term);
    if (matches.length > 0) {
      // Take the highest scoring match
      const bestMatch = matches[0];
      const serviceId = serviceSlugToIdMap[bestMatch.serviceId];
      
      if (serviceId) {
        openBooking(serviceId, bestMatch.serviceName);
      } else {
        // Fallback to search results if ID not found
        navigate(`/services/search?q=${encodeURIComponent(term)}`, {
          state: { selectedAddress }
        });
      }
    } else {
      // If no direct match, navigate to search results
      navigate(`/services/search?q=${encodeURIComponent(term)}`, {
        state: { selectedAddress }
      });
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      {/* Search Form */}
      <form onSubmit={handleSubmit} className="relative">
        <input
          ref={searchRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setShowSuggestions(true)}
          className={`w-full px-6 py-4 pr-16 md:pr-12 rounded-xl text-secondary-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white shadow-lg ${className}`}
          autoComplete="off"
        />
        <button
          type="submit"
          aria-label="Search"
          className={`absolute right-2 md:right-3 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 md:h-10 md:w-10 items-center justify-center rounded-lg transition-colors flex-shrink-0 ${
            variant === 'hero' ? '' : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || matchingServices.length > 0) && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50"
        >
          {/* Text Suggestions */}
          {suggestions.length > 0 && (
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                Suggestions
              </div>
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full text-left px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors ${
                    index === selectedIndex ? 'bg-primary-50 text-primary-600' : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-400">🔍</span>
                    <span>{suggestion}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Matching Services */}
          {matchingServices.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                Services
              </div>
              {matchingServices.map((service, index) => (
                <button
                  key={service.serviceId}
                  onClick={() => handleServiceClick(service)}
                  className={`w-full text-left px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors ${
                    index + suggestions.length === selectedIndex ? 'bg-primary-50 text-primary-600' : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">
                      {service.serviceId === 'plumbing' && <img src={`${process.env.PUBLIC_URL}/plumping.png?v=${Date.now()}`} alt="Plumbing" className="w-4 h-4 object-contain" />}
                      {service.serviceId === 'electrical' && '⚡'}
                      {service.serviceId === 'ac-repair' && '❄️'}
                      {service.serviceId === 'deep-cleaning' && '🧹'}
                      {service.serviceId === 'moving' && '📦'}
                      {service.serviceId === 'painting' && '🎨'}
                      {service.serviceId === 'pest-control' && '🐛'}
                    </span>
                    <div className="flex-1">
                      <div className="font-medium">{service.serviceName}</div>
                      <div className="text-sm text-gray-500">
                        Matched: {service.matchedKeywords.slice(0, 2).join(', ')}
                        {service.matchedKeywords.length > 2 && '...'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      {Math.min(service.score, 100)}% match
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Popular Terms */}
      {showPopularTerms && !showSuggestions && query.length === 0 && (variant === 'hero' && chipSlot ? createPortal(
        <div className="fx-popular">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 px-1">
            <span className="text-sm font-bold whitespace-nowrap mr-2 tracking-tight text-slate-700">
              Popular Search:
            </span>
            {popularTerms.map((term) => (
              <button
                key={term}
                onClick={() => handlePopularTermClick(term)}
                className="text-xs px-4 py-2 rounded-full font-semibold cursor-pointer flex items-center space-x-2 whitespace-nowrap"
                title={`Book ${term} service`}
              >
                <span className="text-base">{getPopularTermIcon(term)}</span>
                <span className="tracking-tight">{term}</span>
              </button>
            ))}
          </div>
        </div>,
        chipSlot
      ) : variant !== 'hero' && (
        <div className="mt-3">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 px-1">
            <span className={`text-sm font-bold whitespace-nowrap mr-2 tracking-tight ${variant === 'white' ? 'text-slate-800' : 'text-white/90'}`}>
              Popular Search:
            </span>
            {popularTerms.map((term) => (
              <button
                key={term}
                onClick={() => handlePopularTermClick(term)}
                className={`text-xs px-4 py-2 rounded-full transition-all duration-200 font-semibold cursor-pointer flex items-center space-x-2 whitespace-nowrap ${
                  variant === 'hero'
                    ? 'bg-white text-slate-800 border border-white shadow-sm'
                    : variant === 'white' 
                    ? 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-float active:scale-95' 
                    : 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm border border-white/10 hover:border-white/20 hover:shadow-lg active:scale-95'
                }`}
                title={`Book ${term} service`}
              >
                <span className="text-base">{getPopularTermIcon(term)}</span>
                <span className="tracking-tight">{term}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PredictiveSearch;
